import React, { useState } from 'react';
import { UsersIcon, BriefcaseIcon, HeartHandshakeIcon, CheckCircleIcon, FileTextIcon, CreditCardIcon, UserIcon, BuildingIcon, CalculatorIcon, HomeIcon, ArrowRightLeftIcon, ArrowRightIcon, LeafIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { AccordionItem } from '../components/AccordionItem';
import { PageHero } from '../components/PageHero';
import { ProcessTimeline } from '../components/ProcessTimeline';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface HypothekenPageProps {
  onNavigate: (page: string) => void;
}
export function HypothekenPage({
  onNavigate
}: HypothekenPageProps) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const faqs = [{
    question: 'Wat kost hypotheekadvies?',
    answer: 'Ik werk op basis van een vast adviestarief dat vooraf met je wordt besproken. Zo weet je precies waar je aan toe bent. Tijdens het gratis kennismakingsgesprek bespreken we de kosten die bij jouw situatie passen.'
  }, {
    question: 'Hoeveel kan ik maximaal lenen?',
    answer: 'Dit hangt af van je inkomen, vaste lasten en persoonlijke situatie. Tijdens een vrijblijvend gesprek berekenen we dit voor je. Gemiddeld duurt dit maar 15 minuten.'
  }, {
    question: 'Kan ik mijn hypotheek oversluiten?',
    answer: 'Ja, vaak is oversluiten erg interessant om je maandlasten te verlagen of om overwaarde op te nemen voor een verbouwing of verduurzaming. Ik reken graag voor je uit of oversluiten in jouw situatie voordelig is, inclusief eventuele boeterente.'
  }, {
    question: 'Wat is de Nationale Hypotheek Garantie (NHG)?',
    answer: 'NHG biedt een vangnet als je je hypotheek niet meer kunt betalen door bijvoorbeeld werkloosheid of scheiding. Bovendien krijg je met NHG vaak een flinke korting op de hypotheekrente. Ik check altijd of je in aanmerking komt voor NHG.'
  }, {
    question: 'Hoe lang duurt het hypotheektraject?',
    answer: 'Van eerste gesprek tot sleuteloverdracht duurt gemiddeld 4 tot 8 weken. Ik zorg dat het proces soepel verloopt en houd je bij elke stap op de hoogte.'
  }, {
    question: 'Wat neem ik mee naar het eerste gesprek?',
    answer: 'Voor het kennismakingsgesprek hoef je niets mee te nemen. Mocht het tot een adviestraject komen, dan vragen we om: loonstroken (laatste 3 maanden), jaaropgave, ID-bewijs, en eventueel je belastingaangifte als ondernemer.'
  }];
  const timelineSteps = [{
    number: 1,
    title: 'Kennismaking',
    description: 'Een vrijblijvend en kosteloos gesprek waarin we jouw situatie, wensen en mogelijkheden in kaart brengen.'
  }, {
    number: 2,
    title: 'Vergelijken',
    description: 'Ik vergelijk de voorwaarden van meer dan 30 geldverstrekkers. Niet alleen op rente, maar ook op flexibiliteit en voorwaarden die bij jouw situatie passen.'
  }, {
    number: 3,
    title: 'Advies & Afsluiten',
    description: 'We bespreken het advies en regelen de complete afhandeling met de bank. Nul stress voor jou.'
  }];
  return <div className="w-full" data-id="element-481">
      {/* Hero */}
      <PageHero title="Hypotheekadvies op maat" subtitle="Van je eerste starterswoning in Amsterdam tot een complexe financiering als ondernemer. Ik maak het overzichtelijk en zoek de beste voorwaarden bij meer dan 30 geldverstrekkers." data-id="element-482" />

      {/* Target Audience Grid (Bento Box) */}
      <section className="py-20 md:py-28 bg-white" data-id="element-483">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-484">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-id="element-485">
            {/* Starters - Large Card */}
            <FadeIn className="md:col-span-8 bg-warm p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-486">
              <div className="flex items-center mb-6" data-id="element-487">
                <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center mr-4" data-id="element-488">
                  <UsersIcon className="w-6 h-6 text-white" data-id="element-489" />
                </div>
                <h2 className="text-3xl font-bold text-navy" data-id="element-490">Starters</h2>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-491">
                Je eerste huis kopen is spannend. Ik bereken je maximale
                leencapaciteit en kijk verder dan de standaard. We onderzoeken
                ook creatieve opties zoals hulp van ouders via een familiebank
                of schenking, zodat je weet waar je aan toe bent.
              </p>
              <ul className="space-y-3" data-id="element-492">
                <li className="flex items-center text-navy font-medium" data-id="element-493">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-494" />{' '}
                  Maximale hypotheek berekening
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-495">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-496" />{' '}
                  Inclusief check op Nationale Hypotheek Garantie (NHG)
                </li>
                <li className="flex items-center text-navy font-medium" data-id="element-497">
                  <CheckCircleIcon className="w-5 h-5 text-gold mr-3" data-id="element-498" /> Opties
                  voor ouderlijke hulp verkennen
                </li>
              </ul>
            </FadeIn>

            {/* Ondernemers - Medium Card */}
            <FadeIn delay={0.1} className="md:col-span-4 bg-navy p-10 rounded-2xl shadow-card text-white" data-id="element-499">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6" data-id="element-500">
                <BriefcaseIcon className="w-6 h-6 text-gold" data-id="element-501" />
              </div>
              <h2 className="text-2xl font-bold mb-4" data-id="element-502">Ondernemers</h2>
              <p className="text-gray-300 leading-relaxed" data-id="element-503">
                Geen vast contract? Geen probleem. Ik lever maatwerk voor
                ZZP'ers en DGA's. Ik weet precies welke banken soepel omgaan met
                wisselende inkomsten en bedrijfsresultaten.
              </p>
            </FadeIn>

            {/* Doorstromers - Medium Card */}
            <FadeIn delay={0.2} className="md:col-span-6 bg-white p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-504">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-505">
                <ArrowRightLeftIcon className="w-6 h-6 text-gold" data-id="element-506" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-507">
                Doorstromers
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-508">
                Ik help je met het slim inzetten van je overwaarde en het
                meenemen van je gunstige rente. Zo maak je de financiële
                overgang naar je volgende woning zo soepel mogelijk.
              </p>
            </FadeIn>

            {/* Oversluiten & Verduurzamen - Medium Card */}
            <FadeIn delay={0.3} className="md:col-span-6 bg-white p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-509">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-510">
                <LeafIcon className="w-6 h-6 text-gold" data-id="element-511" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-512">
                Oversluiten & Verduurzamen
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-513">
                Je maandlasten verlagen of je huis energiezuiniger maken? Ik
                reken uit of oversluiten loont en help je bij het financieren
                van verduurzaming, inclusief inzicht in subsidies en
                energiebespaarleningen.
              </p>
            </FadeIn>

            {/* Scheidingen - Wide Card */}
            <FadeIn delay={0.4} className="md:col-span-12 bg-white p-10 rounded-2xl shadow-card border border-gray-100 flex flex-col md:flex-row items-center gap-8" data-id="element-514">
              <div className="w-16 h-16 bg-warm rounded-full flex items-center justify-center flex-shrink-0" data-id="element-515">
                <HeartHandshakeIcon className="w-8 h-8 text-gold" data-id="element-516" />
              </div>
              <div data-id="element-517">
                <h2 className="text-2xl font-bold text-navy mb-3" data-id="element-518">
                  Scheidingen & Relatiebreuk
                </h2>
                <p className="text-gray-600 leading-relaxed" data-id="element-519">
                  Een emotionele periode waarin snel duidelijkheid nodig is. Wij
                  zoeken in alle rust en met discretie uit of één van jullie in
                  de huidige woning kan blijven wonen. Ik neem het financiële
                  traject volledig uit handen, zodat jullie je kunnen focussen
                  op de toekomst.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <ProcessTimeline title="Hoe wij werken" steps={timelineSteps} data-id="element-520" />

      {/* CTA after timeline */}
      <section className="pb-20 md:pb-28 bg-warm" data-id="element-521">
        <div className="text-center" data-id="element-522">
          <FadeIn data-id="element-523">
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-524">
              Plan een gratis kennismaking
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 md:py-28 bg-white" data-id="element-525">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-526">
          <SectionHeading title="Veelgestelde Vragen" subtitle="Alles wat je moet weten over het hypotheekproces." data-id="element-527" />

          <FadeIn delay={0.2} className="bg-warm rounded-2xl shadow-sm p-6 md:p-10 border border-gray-100" data-id="element-528">
            {faqs.map((faq, index) => <AccordionItem key={index} question={faq.question} answer={faq.answer} isOpen={openAccordion === index} onClick={() => setOpenAccordion(openAccordion === index ? null : index)} data-id="element-529" />)}
          </FadeIn>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-20 md:py-28 bg-navy text-white" data-id="element-530">
        <div className="max-w-5xl mx-auto px-4 md:px-8" data-id="element-531">
          <SectionHeading title="Checklist eerste gesprek" subtitle="Voor een vrijblijvende kennismaking hoef je niets mee te nemen. Gaan we verder met een adviestraject? Dan hebben we vaak de volgende documenten nodig:" light={true} data-id="element-532" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-533">
            <FadeIn delay={0.1} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-534">
              <CreditCardIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-535" />
              <span className="font-medium" data-id="element-536">Loonstroken (laatste 3 mnd)</span>
            </FadeIn>
            <FadeIn delay={0.2} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-537">
              <FileTextIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-538" />
              <span className="font-medium" data-id="element-539">Jaaropgave werkgever</span>
            </FadeIn>
            <FadeIn delay={0.3} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-540">
              <UserIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-541" />
              <span className="font-medium" data-id="element-542">Geldig ID-bewijs</span>
            </FadeIn>
            <FadeIn delay={0.4} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-543">
              <BuildingIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-544" />
              <span className="font-medium" data-id="element-545">Werkgeversverklaring</span>
            </FadeIn>
            <FadeIn delay={0.5} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-546">
              <CalculatorIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-547" />
              <span className="font-medium" data-id="element-548">
                Belastingaangifte (ondernemers)
              </span>
            </FadeIn>
            <FadeIn delay={0.6} className="bg-white/10 p-6 rounded-2xl border border-white/20 flex items-center gap-4" data-id="element-549">
              <HomeIcon className="w-8 h-8 text-gold flex-shrink-0" data-id="element-550" />
              <span className="font-medium" data-id="element-551">Eventueel: koopovereenkomst</span>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Cross-page CTA */}
      <section className="py-16 bg-white" data-id="element-552">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-553">
          <FadeIn data-id="element-554">
            <p className="text-gray-500 mb-3" data-id="element-555">
              Op zoek naar woonbootfinanciering?
            </p>
            <Button variant="ghost" onClick={() => onNavigate('woonboten')} data-id="element-556">
              Bekijk onze woonboot-expertise
            </Button>
          </FadeIn>
        </div>
      </section>
    </div>;
}